import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, Link } from "react-router-dom";
import { MessageSquare } from "lucide-react";
import { AuthProvider } from "./contexts/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { Loading } from "./components/Loading";
import { RouteErrorBoundary } from "./components/RouteErrorBoundary";
import { Button } from "@/components/ui/button";
import { SMS_UI_DISABLED_MESSAGE, isSmsUiEnabled } from "@/config/smsFeature";
import { retryDynamicImport } from "./utils/retryDynamicImport";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import InviteRequired from "./pages/InviteRequired";
import Login from "./pages/Login";
import ResetPassword from "./pages/ResetPassword";
import { EmailConfirmation } from "./pages/EmailConfirmation";
// Lazy load additional pages that are not critical to initial load
const Dashboard = lazy(() => retryDynamicImport(() => import("./pages/Dashboard")));
const TransactionList = lazy(() => retryDynamicImport(() => import("./pages/TransactionList")));
const UserProfile = lazy(() => retryDynamicImport(() => import("./pages/UserProfile")));
const Contacts = lazy(() => retryDynamicImport(() => import("./pages/Contacts")));
const FaqPage = lazy(() => retryDynamicImport(() => import("./pages/FaqPage")));
import { PostHogProvider } from "@posthog/react";
import posthog from "posthog-js";
import { ThemeProvider } from "@/components/ThemeProvider";
// Deals subscription pages (only billing surface; legacy /tokens/* removed in
// Slice I3, but the routes still exist as redirects so any cached link or
// bookmark from before the cutover continues to work).
const SubscribePage = lazy(() => retryDynamicImport(() => import("./pages/subscribe/SubscribePage")));
const SubscribeSuccess = lazy(() => retryDynamicImport(() => import("./pages/subscribe/SubscribeSuccess")));
const DealHistory = lazy(() => retryDynamicImport(() => import("./pages/deals/DealHistory")));
import { DevTools } from "./components/dev/DevTools";
import { DevToolsProvider } from "./contexts/DevToolsContext";

// Lazy load heavy components for better performance
const AdminDashboard = lazy(() => retryDynamicImport(() => import("./pages/admin/AdminDashboard")));
const TransactionOverview = lazy(() => retryDynamicImport(() => import("./pages/TransactionOverview")));
const Communication = lazy(() => retryDynamicImport(() => import("./pages/Communication")));
const SmsTemplates = lazy(() => retryDynamicImport(() => import("./pages/SmsTemplates")));
const CoordimateFlow = lazy(() => retryDynamicImport(() => import("./pages/CoordimateFlow")));
const Configuration = lazy(() => retryDynamicImport(() => import("./pages/Configuration")));
const OrganizationManagement = lazy(() => retryDynamicImport(() => import("./pages/OrganizationManagement")));
const Marketing = lazy(() => retryDynamicImport(() => import("./pages/Marketing")));
const CreateTransaction = lazy(() => retryDynamicImport(() => import("./pages/CreateTransaction")));
const TransactionLaunch = lazy(() => retryDynamicImport(() => import("./pages/TransactionLaunch")));
// Dev-only pages
const DevTransactionReview = lazy(() => retryDynamicImport(() => import("./pages/dev/DevTransactionReview")));
const DevTransactionOverview = lazy(() => retryDynamicImport(() => import("./pages/dev/DevTransactionOverview")));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

const BASENAME = import.meta.env.BASE_URL.replace(/\/$/, "");

function SmsUnavailable() {
  return (
    <div className="flex min-h-[420px] items-center justify-center px-4">
      <div className="w-full max-w-md rounded-lg border bg-card p-6 text-center shadow-sm">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
          <MessageSquare className="h-6 w-6 text-muted-foreground" />
        </div>
        <h1 className="text-lg font-semibold">SMS unavailable</h1>
        <p className="mt-2 text-sm text-muted-foreground">{SMS_UI_DISABLED_MESSAGE}</p>
        <Button asChild className="mt-5" variant="outline">
          <Link to="/communication">Back to templates</Link>
        </Button>
      </div>
    </div>
  );
}

function App() {
  return (
    <PostHogProvider client={posthog}>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <DevToolsProvider>
            <AuthProvider>
              <ThemeProvider>
                <BrowserRouter basename={BASENAME}>
                <DevTools />
                <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/invite-required" element={<InviteRequired />} />
                <Route path="/login" element={<Login />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/register" element={<Navigate to="/invite-required" replace />} />
                <Route path="/email-confirmation" element={<EmailConfirmation />} />

                {/* Protected routes */}
                <Route path="/dashboard" element={
                  <ProtectedRoute>
                    <RouteErrorBoundary routeName="Dashboard">
                      <Suspense fallback={<Loading message="Loading Dashboard..." />}>
                        <Dashboard />
                      </Suspense>
                    </RouteErrorBoundary>
                  </ProtectedRoute>
                } />
                
                {/* Marketing page - protected but doesn't redirect */}
                <Route path="/marketing" element={
                  <ProtectedRoute>
                    <RouteErrorBoundary routeName="Marketing">
                      <Suspense fallback={<Loading message="Loading Marketing..." />}>
                        <Marketing />
                      </Suspense>
                    </RouteErrorBoundary>
                  </ProtectedRoute>
                } />
                
                <Route path="/transactions" element={
                  <ProtectedRoute>
                    <RouteErrorBoundary routeName="Transactions">
                      <Suspense fallback={<Loading message="Loading Transactions..." />}>
                        <TransactionList />
                      </Suspense>
                    </RouteErrorBoundary>
                  </ProtectedRoute>
                } />
                <Route path="/communication" element={
                  <ProtectedRoute>
                    <RouteErrorBoundary routeName="Communication">
                      <Suspense fallback={<Loading message="Loading Communication..." />}>
                        <Communication />
                      </Suspense>
                    </RouteErrorBoundary>
                  </ProtectedRoute>
                } />
                <Route path="/communication/templates/new" element={
                  <ProtectedRoute>
                    <RouteErrorBoundary routeName="Communication">
                      <Suspense fallback={<Loading message="Loading Communication..." />}>
                        <Communication />
                      </Suspense>
                    </RouteErrorBoundary>
                  </ProtectedRoute>
                } />
                <Route path="/communication/templates/:id/edit" element={
                  <ProtectedRoute>
                    <RouteErrorBoundary routeName="Communication">
                      <Suspense fallback={<Loading message="Loading Communication..." />}>
                        <Communication />
                      </Suspense>
                    </RouteErrorBoundary>
                  </ProtectedRoute>
                } />
                <Route path="/communication/sms" element={
                  <ProtectedRoute>
                    <RouteErrorBoundary routeName="SMS Templates">
                      <Suspense fallback={<Loading message="Loading SMS Templates..." />}>
                        {isSmsUiEnabled() ? <SmsTemplates /> : <SmsUnavailable />}
                      </Suspense>
                    </RouteErrorBoundary>
                  </ProtectedRoute>
                } />
                <Route path="/communication/sms/new" element={
                  <ProtectedRoute>
                    <RouteErrorBoundary routeName="SMS Templates">
                      <Suspense fallback={<Loading message="Loading SMS Templates..." />}>
                        {isSmsUiEnabled() ? <SmsTemplates /> : <SmsUnavailable />}
                      </Suspense>
                    </RouteErrorBoundary>
                  </ProtectedRoute>
                } />
                <Route path="/communication/sms/:id/edit" element={
                  <ProtectedRoute>
                    <RouteErrorBoundary routeName="SMS Templates">
                      <Suspense fallback={<Loading message="Loading SMS Templates..." />}>
                        {isSmsUiEnabled() ? <SmsTemplates /> : <SmsUnavailable />}
                      </Suspense>
                    </RouteErrorBoundary>
                  </ProtectedRoute>
                } />
                <Route path="/coordimate-flow" element={
                  <ProtectedRoute>
                    <RouteErrorBoundary routeName="CoordiMate Flow">
                      <Suspense fallback={<Loading message="Loading CoordiMate Flow..." />}>
                        <CoordimateFlow />
                      </Suspense>
                    </RouteErrorBoundary>
                  </ProtectedRoute>
                } />
                <Route path="/transactions/new" element={
                  <ProtectedRoute>
                    <RouteErrorBoundary routeName="Create Transaction">
                      <Suspense fallback={<Loading message="Loading Transaction Form..." />}>
                        <CreateTransaction />
                      </Suspense>
                    </RouteErrorBoundary>
                  </ProtectedRoute>
                } />
                <Route path="/transactions/:id" element={
                  <ProtectedRoute>
                    <RouteErrorBoundary routeName="Transaction Overview">
                      <Suspense fallback={<Loading message="Loading Transaction..." />}>
                        <TransactionOverview />
                      </Suspense>
                    </RouteErrorBoundary>
                  </ProtectedRoute>
                } />
                <Route path="/transactions/:id/launch" element={
                  <ProtectedRoute>
                    <RouteErrorBoundary routeName="Transaction Launch">
                      <Suspense fallback={<Loading message="Loading Launch Flow..." />}>
                        <TransactionLaunch />
                      </Suspense>
                    </RouteErrorBoundary>
                  </ProtectedRoute>
                } />
                <Route path="/profile" element={
                  <ProtectedRoute>
                    <RouteErrorBoundary routeName="Profile">
                      <Suspense fallback={<Loading message="Loading Profile..." />}>
                        <UserProfile />
                      </Suspense>
                    </RouteErrorBoundary>
                  </ProtectedRoute>
                } />
                <Route path="/contacts" element={
                  <ProtectedRoute>
                    <RouteErrorBoundary routeName="Contacts">
                      <Suspense fallback={<Loading message="Loading Contacts..." />}>
                        <Contacts />
                      </Suspense>
                    </RouteErrorBoundary>
                  </ProtectedRoute>
                } />
                <Route path="/faq" element={
                  <ProtectedRoute>
                    <RouteErrorBoundary routeName="FAQ">
                      <Suspense fallback={<Loading message="Loading FAQ..." />}>
                        <FaqPage />
                      </Suspense>
                    </RouteErrorBoundary>
                  </ProtectedRoute>
                } />
                <Route path="/organization" element={
                  <ProtectedRoute requiresAdmin>
                    <RouteErrorBoundary routeName="Organization">
                      <Suspense fallback={<Loading message="Loading Organization..." />}>
                        <OrganizationManagement />
                      </Suspense>
                    </RouteErrorBoundary>
                  </ProtectedRoute>
                } />

                {/*
                 * Billing routes — single mode (deals subscription model).
                 * The /tokens/* paths are kept as redirects to the canonical
                 * deals routes so any cached link, bookmark, or external
                 * reference from before the token-to-deals migration still
                 * resolves cleanly.
                 */}
                <Route path="/subscribe" element={
                  <ProtectedRoute>
                    <Suspense fallback={<Loading message="Loading..." />}>
                      <SubscribePage />
                    </Suspense>
                  </ProtectedRoute>
                } />
                <Route path="/subscribe/success" element={
                  <ProtectedRoute>
                    <Suspense fallback={<Loading message="Loading..." />}>
                      <SubscribeSuccess />
                    </Suspense>
                  </ProtectedRoute>
                } />
                <Route path="/deals/history" element={
                  <ProtectedRoute>
                    <Suspense fallback={<Loading message="Loading..." />}>
                      <DealHistory />
                    </Suspense>
                  </ProtectedRoute>
                } />
                <Route path="/tokens/purchase" element={<Navigate to="/subscribe" replace />} />
                <Route path="/tokens/purchase/success" element={<Navigate to="/subscribe/success" replace />} />
                <Route path="/tokens/history" element={<Navigate to="/deals/history" replace />} />

                {/* Placeholder protected routes */}
                <Route path="/calendar" element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                } />
                <Route path="/communications" element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                } />
                <Route path="/activity" element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                } />
                <Route path="/compliance" element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                } />
                <Route path="/reports" element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                } />
                <Route path="/integrations" element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                } />
                <Route path="/portal" element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                } />
                <Route path="/configuration" element={
                  <ProtectedRoute requiresAdmin>
                    <RouteErrorBoundary routeName="Configuration">
                      <Suspense fallback={<Loading message="Loading Configuration..." />}>
                        <Configuration />
                      </Suspense>
                    </RouteErrorBoundary>
                  </ProtectedRoute>
                } />
                <Route path="/configuration/:templateId" element={
                  <ProtectedRoute requiresAdmin>
                    <RouteErrorBoundary routeName="Configuration">
                      <Suspense fallback={<Loading message="Loading Configuration..." />}>
                        <Configuration />
                      </Suspense>
                    </RouteErrorBoundary>
                  </ProtectedRoute>
                } />
                <Route path="/settings" element={<Navigate to="/configuration" replace />} />

                {/* Admin Dashboard - Coordi Team Only */}
                <Route path="/admin" element={
                  <ProtectedRoute requiresFounder>
                    <RouteErrorBoundary routeName="Admin Dashboard">
                      <Suspense fallback={<Loading message="Loading Admin Dashboard..." />}>
                        <AdminDashboard />
                      </Suspense>
                    </RouteErrorBoundary>
                  </ProtectedRoute>
                } />

                {/* Dev Routes - Only available in development mode */}
                {import.meta.env.DEV && (
                  <>
                    <Route path="/dev/review" element={
                      <ProtectedRoute>
                        <RouteErrorBoundary routeName="Dev Review">
                          <Suspense fallback={<Loading message="Loading Dev Review..." />}>
                            <DevTransactionReview />
                          </Suspense>
                        </RouteErrorBoundary>
                      </ProtectedRoute>
                    } />
                    <Route path="/dev/transaction" element={
                      <ProtectedRoute>
                        <RouteErrorBoundary routeName="Dev Transaction">
                          <Suspense fallback={<Loading message="Loading Mock Transaction..." />}>
                            <DevTransactionOverview />
                          </Suspense>
                        </RouteErrorBoundary>
                      </ProtectedRoute>
                    } />
                  </>
                )}

                {/* Legacy route */}
                <Route path="/index" element={<Index />} />
                
                {/* Catch-all route */}
                <Route path="*" element={<NotFound />} />
              </Routes>
                </BrowserRouter>
                <Toaster />
                <Sonner />
              </ThemeProvider>
            </AuthProvider>
          </DevToolsProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </PostHogProvider>
  );
}

export default App;
